import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SxProps, alpha, styled, useTheme } from '@mui/material/styles';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useState } from 'react';

import CloseButton from 'reservations/base/components/CloseButton';
import useContrastTextColor from 'reservations/brand/hooks/useContrastTextColor';
import {
  TOO_MANY_ATTEMPTS_MESSAGE,
  VERIFICATION_CODE_LENGTH
} from 'reservations/dinerAccount/constants';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';
import { beginDinerAuth } from 'reservations/dinerAccount/queries/beginDinerAuth';
import { useBookingTypes } from 'reservations/form/hooks/useBookingTypes';

interface DinerAuthenticationInputProps {
  onClose?: () => void;
  onSubmit?: () => void;
  // The V2 Drawers (so far the ones opened from the predining page) already have a header
  // so we should hide this one
  hideHeader?: boolean;
  sxOverride?: SxProps;
  sxInputOverride?: SxProps;
  errorColorOverride?: SxProps;
}

export const DinerAuthenticationInput = ({
  onClose,
  onSubmit,
  hideHeader = false,
  sxOverride = {},
  sxInputOverride = {},
  errorColorOverride = {}
}: DinerAuthenticationInputProps) => {
  const {
    dinerEmail,
    isSmsAuthDisabled,
    lastThreeDigitsOfPhone,
    submitVerificationCode,
    verificationCodeErrorMessage
  } = useDinerAccount();
  const { isJoin, isModify } = useBookingTypes();
  const [verificationCode, setVerificationCode] = useState('');
  const contrastColor = useContrastTextColor();
  const theme = useTheme();

  const dinerAuthHasError = !!verificationCodeErrorMessage || isSmsAuthDisabled;

  const handleOnChange = (value: string) => {
    setVerificationCode(value);
  };
  const handleOnComplete = async (verificationCodeValue: string) => {
    await submitVerificationCode(verificationCodeValue);

    onSubmit?.();
  };

  const validateChar = (value: string) => !isNaN(Number(value));

  let body, header;
  const defaultBodyText = `We just sent a text message verification code to (•••) •••-•${lastThreeDigitsOfPhone}. Enter the six-digit code below to`;

  switch (true) {
    case isSmsAuthDisabled:
      body = 'We ran into an issue while looking up your account.';
      break;
    case isJoin:
      header = '';
      body = `${defaultBodyText} join the party.`;
      break;
    case isModify:
      header = 'Enter your verification code';
      body = `${defaultBodyText} make changes to your reservation.`;
      break;
    case hideHeader:
      header = '';
      body = `${defaultBodyText} retrieve your information.`;
      break;
    default:
      header = 'You have saved information!';
      body = `${defaultBodyText} retrieve your information.`;
  }

  return (
    <>
      <Grid
        item
        sx={{
          backgroundColor: contrastColor,
          borderRadius: '4px',
          ...sxOverride
        }}
      >
        <DinerAuthOuterContainer data-testid="one-time-password-input">
          {(!!header || !!onClose) && (
            <Grid container direction="row" alignItems="center">
              <Grid item>
                <DinerAuthHeaderText>{header}</DinerAuthHeaderText>
              </Grid>
              {onClose && (
                <Grid item sx={{ mt: 'auto', ml: 'auto' }}>
                  <CloseButton onClick={onClose} />
                </Grid>
              )}
            </Grid>
          )}
          <DinerAuthBodyText>{body}</DinerAuthBodyText>
          <Box>
            <MuiOtpInput
              value={verificationCode}
              onChange={handleOnChange}
              length={VERIFICATION_CODE_LENGTH}
              onComplete={handleOnComplete}
              validateChar={validateChar}
              TextFieldsProps={{
                error: !!verificationCodeErrorMessage
              }}
              autoFocus
              sx={{
                gap: '8px',
                '.MuiOtpInput-TextField': {
                  background: 'transparent',
                  '.MuiOutlinedInput-root': {
                    borderRadius: '2px',
                    '.MuiOutlinedInput-input': {
                      color: theme.typography.body1.color // text color inside the boxes
                    },
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(contrastColor, 0.6) // box border color
                    }
                  }
                },
                ...sxInputOverride,

                ...(dinerAuthHasError && {
                  '& .MuiOtpInput-TextField .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor:
                        (errorColorOverride as any)?.color ??
                        theme.palette.error.main
                    }
                })
              }}
            />
            {dinerAuthHasError && (
              <Typography
                className="auth-error-message"
                sx={theme => ({
                  fontSize: '14px',
                  mt: '8px',
                  color:
                    (errorColorOverride as any)?.color ??
                    theme.palette.error.main
                })}
              >
                {isSmsAuthDisabled
                  ? TOO_MANY_ATTEMPTS_MESSAGE
                  : verificationCodeErrorMessage}
              </Typography>
            )}
          </Box>
          <ResendCodeButton
            disabled={isSmsAuthDisabled}
            dinerEmail={dinerEmail}
          />
        </DinerAuthOuterContainer>
      </Grid>
    </>
  );
};

const DinerAuthOuterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.default, 0.95),
  padding: '24px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px'
}));
const DinerAuthHeaderText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 1.11
});
const DinerAuthBodyText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 450,
  lineHeight: 1.25
});

type ResendCodeButtonProps = {
  dinerEmail: string;
  disabled?: boolean;
};

const ResendCodeButton = ({ dinerEmail, disabled }: ResendCodeButtonProps) => (
  <Typography
    sx={{
      textDecoration: 'underline',
      textUnderlineOffset: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      textTransform: 'uppercase',
      opacity: disabled ? '0.4' : '1'
    }}
    onClick={() => {
      !disabled && beginDinerAuth(dinerEmail, true);
    }}
  >
    Resend Code
  </Typography>
);
